import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { LearningSectionService } from '../../learning-section.service';
import { LearningSectionJsonModel } from '../../learning-section.json.model';
import { LearningSectionActions } from './learning-section.actions';
import { LearningSectionEditComponent } from '../../learning-section-edit/learning-section-edit.component';
import { FetchOptionsStateModel } from '../../../core/state/fetchOptions.state';
import {
  LearningSectionContainerEditComponent
} from '../../learning-section-container/learning-section-container-edit/learning-section-container-edit.component';
import {
  LearningSectionContainerJsonModel
} from '../../learning-section-container/learning-section-container.json.model';

import { CollectionFilter, IFetchOptions } from '../../../core/jsonApi/BaseCollectionService';
import { IFilters } from '../../../utils/component/base-filter-component/IFilter';

export interface LearningSectionStateModel {
  learningSections: LearningSectionJsonModel[]
  learningSection: LearningSectionJsonModel | null
  learningSectionContainers: LearningSectionContainerJsonModel[]
  loadedPages: number
}

@State<LearningSectionStateModel>({
  name: 'learningSection',
  defaults: {
    learningSections: [],
    learningSection: null,
    learningSectionContainers: [],
    loadedPages: 0
  }
})
@Injectable()
export class LearningSectionState {
  private readonly learningSectionService = inject(LearningSectionService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: LearningSectionStateModel) {
    return state;
  }

  @Selector()
  static getLearningSections(state: LearningSectionStateModel) {
    return state.learningSections;
  }

  @Selector()
  static getLearningSection(state: LearningSectionStateModel) {
    return state.learningSection;
  }

  @Selector()
  static getLearningSectionContainers(state: LearningSectionStateModel) {
    return state.learningSection?.learningSectionContainers ?? [];
  }

  @Selector()
  static getAmountOfLearningSections(state: LearningSectionStateModel) {
    return state.learningSections.length;
  }

  @Selector()
  static learningSectionsIsEmpty(state: LearningSectionStateModel) {
    return state.learningSections.length <= 0;
  }

  @Selector()
  static loadedPages(state: LearningSectionStateModel) {
    return state.loadedPages;
  }

  @Selector()
  static getSortedLearningSections(state: LearningSectionStateModel): LearningSectionJsonModel[] {
    return [...state.learningSections].sort((a, b) => {
      return a.title.localeCompare(b.title, 'de', { sensitivity: 'base', numeric: true });
    });
  }

  @Action(LearningSectionActions.Fetch)
  onFetchLearningSections(ctx: StateContext<LearningSectionStateModel>, action: LearningSectionActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides,
      sortFieldName: 'title'
    };

    return this.learningSectionService.getAllWithPagination({
      queryParams: {
        include: action.include
      }
    }, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new LearningSectionActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(LearningSectionActions.FetchWithFilters)
  onFetchWithFiltersLearningSections(ctx: StateContext<LearningSectionStateModel>, action: LearningSectionActions.FetchWithFilters) {
    const filtersArray: CollectionFilter[] = Object.entries(action.filters).map(([key, value]) => ({
      fieldName: key,
      value: value as string | boolean
    }));

    const fetchOptions: IFetchOptions = {
      filters: filtersArray
    };

    return this.learningSectionService.getAllWithPagination({
      queryParams: {
        filter: action.filters,
        include: 'subjects,consumer,learningSectionLayout',
      }
    }, fetchOptions).pipe(
      tap(res => {
        ctx.dispatch(new LearningSectionActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(LearningSectionActions.FetchSuccess)
  onFetchLearningSectionsSuccess(ctx: StateContext<LearningSectionStateModel>, { payload }: LearningSectionActions.FetchSuccess) {
    ctx.setState({ ...ctx.getState(), learningSections: payload });
  }

  @Action(LearningSectionActions.Add)
  onAdd(ctx: StateContext<LearningSectionStateModel>, { payload, filters }: LearningSectionActions.Add) {
    return this.dialog.open<LearningSectionEditComponent, LearningSectionJsonModel, LearningSectionJsonModel>(LearningSectionEditComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((learningSection): learningSection is LearningSectionJsonModel => !!learningSection),
        switchMap(learningSection => this.learningSectionService.createAndSave(learningSection)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new LearningSectionActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new LearningSectionActions.Fetch());
          }
        })
      );
  }

  @Action(LearningSectionActions.Edit)
  onEdit(ctx: StateContext<LearningSectionStateModel>, { payload, filters }: LearningSectionActions.Edit) {
    return this.dialog
      .open<LearningSectionEditComponent, LearningSectionJsonModel, LearningSectionJsonModel>(LearningSectionEditComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((learningSection): learningSection is LearningSectionJsonModel => !!learningSection),
        switchMap(learningSection => this.learningSectionService.update(learningSection)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new LearningSectionActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new LearningSectionActions.Fetch());
          }
        })
      );
  }

  @Action(LearningSectionActions.EditRaw)
  onEditRaw(ctx: StateContext<LearningSectionStateModel>, { payload, filters }: LearningSectionActions.EditRaw) {
    return this.learningSectionService.update(payload)
      .pipe(
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new LearningSectionActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new LearningSectionActions.Fetch());
          }
        }),
        catchError((error) => {
          console.error('Update failed:', error);
          return of();
        })
      );
  }

  @Action(LearningSectionActions.EditContainer)
  onEditContainer(ctx: StateContext<LearningSectionStateModel>, { payload, filters }: LearningSectionActions.EditContainer) {
    return this.dialog.open<LearningSectionContainerEditComponent, LearningSectionJsonModel & { filters?: IFilters }, LearningSectionJsonModel>(LearningSectionContainerEditComponent,
      {
        data: { ...payload, filters } as LearningSectionJsonModel & { filters?: IFilters },
        width: '90vw',
        height: '90vh',
        maxWidth: '90vw',
        panelClass: 'learning-section-container-full-screen-dialog'
      })
      .afterClosed()
      .pipe(
        filter((learningSection): learningSection is LearningSectionJsonModel => !!learningSection),
        switchMap(learningSection => this.learningSectionService.update(learningSection)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new LearningSectionActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new LearningSectionActions.Fetch());
          }
        })
      );
  }

  @Action(LearningSectionActions.EditContainer)
  onFetchLearningSectionOnEditContainer(ctx: StateContext<LearningSectionStateModel>, { payload }: LearningSectionActions.EditContainer) {
    ctx.dispatch(new LearningSectionActions.FetchOne(payload.id));
  }

  @Action(LearningSectionActions.Delete)
  onDelete(ctx: StateContext<LearningSectionJsonModel>, { payload }: LearningSectionActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.name,
        itemType: 'Lernabschnitt',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new LearningSectionActions.Fetch());
        })
      );
  }

  @Action(LearningSectionActions.FetchOne)
  onFetchOneLearningSection(ctx: StateContext<LearningSectionStateModel>, action: LearningSectionActions.FetchOne) {
    return this.learningSectionService.getOneWithId(action.id, {
      queryParams: {
        include: 'learningSectionContainers.content'
      }
    }).pipe(
      tap((res: LearningSectionJsonModel) => {
        ctx.dispatch(new LearningSectionActions.FetchOneSuccess(res));
        const learningSectionContainers = res.learningSectionContainers || [];
        ctx.dispatch(new LearningSectionActions.UpdateLearningSectionContainers(learningSectionContainers));
      }),
      catchError((error) => {
        return of(new LearningSectionActions.FetchOneFailure(error));
      })
    );
  }

  @Action(LearningSectionActions.FetchOneSuccess)
  onFetchOneSuccess(ctx: StateContext<LearningSectionStateModel>, { payload }: LearningSectionActions.FetchOneSuccess) {
    ctx.setState({ ...ctx.getState(), learningSection: payload });
  }

  @Action(LearningSectionActions.FetchOneFailure)
  onFetchOneFailure(ctx: StateContext<LearningSectionStateModel>, { error }: LearningSectionActions.FetchOneFailure) {
    console.error('Failed to fetch learning section:', error);
  }

  @Action(LearningSectionActions.FetchAppend)
  onFetchAppendSequences(ctx: StateContext<LearningSectionStateModel>, action: LearningSectionActions.FetchAppend) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.learningSectionService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new LearningSectionActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(LearningSectionActions.FetchSuccessAppend)
  onFetchLearningSectionsSuccessAppend(ctx: StateContext<LearningSectionStateModel>, { payload }: LearningSectionActions.FetchSuccess) {
    const state = ctx.getState();
    const nextPage = state.loadedPages + 1;

    ctx.patchState({
      learningSections: [...state.learningSections, ...payload],
      learningSection: state.learningSection,
      learningSectionContainers: [...state.learningSectionContainers],
      loadedPages: nextPage
    });
  }

  @Action(LearningSectionActions.UpdateLearningSectionContainers)
  onUpdateLearningSectionContainers(ctx: StateContext<LearningSectionStateModel>, { payload }: LearningSectionActions.UpdateLearningSectionContainers) {
    ctx.patchState({
      learningSectionContainers: payload
    });
  }
}
